import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Switch, FormControlLabel, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, alpha, useTheme } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VerifiedIcon from '@mui/icons-material/Verified';
import AIGearIcon from '../components/AIGearIcon';
import ActiveReminders from '../components/ActiveReminders';
import AssistantSettingsDialog, { ASSISTANT_FUNCTIONS, functionOn, AssistantFunctions, FunctionPrompt, promptCopy } from '../components/AssistantSettingsDialog';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useCopilot } from '../contexts/CopilotContext';
import config from '../config';

const RemindersPage: React.FC = () => {
  const navigate = useNavigate();
  const { palette } = useTheme();
  const { showMessage, clearMessage } = useCopilot();
  const [, setCalendarConnected] = useState<boolean | null>(null);
  const [gmailConnected, setGmailConnected] = useState<boolean | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [certCount, setCertCount] = useState<number | null>(null);
  const [providerCount, setProviderCount] = useState<number | null>(null);
  const mountGmailCheckDoneRef = useRef(false);
  const [agenticMode, setAgenticMode] = useState(() => {
    const saved = localStorage.getItem('agenticMode');
    return saved === 'true';
  });
  const [agenticStep, setAgenticStep] = useState(0);
  const [agenticRunning, setAgenticRunning] = useState(false);
  const [lastRunResults, setLastRunResults] = useState<{ reminders: number; emails: number; scheduled: number } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [leadDays, setLeadDays] = useState(21);
  const [flowRecipients, setFlowRecipients] = useState<Record<string, 'both' | 'provider' | 'tenant' | 'none'>>({});
  const [assistantFunctions, setAssistantFunctions] = useState<AssistantFunctions>({});
  const [functionPrompt, setFunctionPrompt] = useState<FunctionPrompt | null>(null);
  const [functionBusy, setFunctionBusy] = useState(false);

  const handleToggleFunction = async (key: string, enable: boolean) => {
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const next = { ...assistantFunctions, [key]: enable };
    setFunctionBusy(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/assistant/settings`, {
        method: 'PUT', headers,
        body: JSON.stringify({ functions: Object.fromEntries(ASSISTANT_FUNCTIONS.map(f => [f.key, functionOn(next, f.key)])) }),
      });
      if (!res.ok) throw new Error();
      setAssistantFunctions(next);
      setRefreshTrigger(p => p + 1);
      if (key === 'calendar' || key === 'contacts') {
        const impactRes = await fetch(`${config.apiUrl}/api/assistant/${key}-impact?enabled=${enable}`, { headers });
        const count = impactRes.ok ? (await impactRes.json()).count : 0;
        if (count > 0) setFunctionPrompt({ key, enable, count });
      }
    } catch {
      showMessage({ text: 'I could not save that setting. Please try again.' });
    } finally {
      setFunctionBusy(false);
    }
  };

  const applyFunctionPrompt = async (apply: boolean) => {
    const prompt = functionPrompt;
    setFunctionPrompt(null);
    if (!prompt || !apply) return;
    setFunctionBusy(true);
    try {
      await fetch(`${config.apiUrl}/api/assistant/apply-${prompt.key}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify({ enabled: prompt.enable }),
      });
      setRefreshTrigger(p => p + 1);
    } finally {
      setFunctionBusy(false);
    }
  };

  const [flowCounts, setFlowCounts] = useState<{ active: number; paused: number } | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    fetch(`${config.apiUrl}/api/reminders/active-with-certificates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.reminders) return;
        const paused = data.reminders.filter((r: any) => r.copilot_paused || r.recipient_mode === 'none').length;
        setFlowCounts({ active: data.reminders.length - paused, paused });
      })
      .catch(() => {});
  }, [refreshTrigger, agenticMode]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    fetch(`${config.apiUrl}/api/assistant/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.reminder_lead_days) setLeadDays(data.reminder_lead_days);
        if (data?.flow_recipients) setFlowRecipients(data.flow_recipients);
        if (data?.functions) setAssistantFunctions(data.functions);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    fetch(`${config.apiUrl}/api/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const v = data.agentic_mode_enabled || false;
        setAgenticMode(v);
        localStorage.setItem('agenticMode', String(v));
      })
      .catch(() => {});

    fetch(`${config.apiUrl}/api/auth/google/scopes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const authed = !!(data?.success && data?.is_authenticated);
        setCalendarConnected(authed && !!data?.services?.calendar);
        setGmailConnected(authed && !!data?.services?.gmail);
      })
      .catch(() => { setCalendarConnected(false); setGmailConnected(false); });

    fetch(`${config.apiUrl}/api/certificates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.certificates) setCertCount(data.certificates.length); })
      .catch(() => {});

    fetch(`${config.apiUrl}/api/certificate-providers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.providers) setProviderCount(data.providers.length); })
      .catch(() => {});
  }, []);

  const handleGoogleConnect = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      const res = await fetch(`${config.apiUrl}/api/auth/google/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ redirect_uri: `${window.location.origin}/auth/google/callback` }),
      });
      const data = await res.json();
      if (!data.success || !data.auth_url) return;
      const tab = window.open(data.auth_url, '_blank');
      if (!tab) return;
      const poll = setInterval(async () => {
        if (!tab.closed) return;
        clearInterval(poll);
        try { await fetch(`${config.apiUrl}/api/gmail/setup-watch`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); } catch {}
        const r = await fetch(`${config.apiUrl}/api/auth/google/scopes`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          const authed = !!(d?.success && d?.is_authenticated);
          setCalendarConnected(authed && !!d?.services?.calendar);
          setGmailConnected(authed && !!d?.services?.gmail);
        }
        clearMessage();
      }, 500);
      setTimeout(() => clearInterval(poll), 300000);
    } catch (err) {
      console.error('Google connect error:', err);
    }
  };

  // Auto-disable agentic mode if Gmail is disconnected when page loads
  useEffect(() => {
    if (gmailConnected === null) return;
    if (mountGmailCheckDoneRef.current) return;
    mountGmailCheckDoneRef.current = true;
    if (!agenticMode || gmailConnected) return;

    const token = localStorage.getItem('auth_token');
    fetch(`${config.apiUrl}/api/users/settings/agentic-mode`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ enabled: false }),
    }).then(() => {
      setAgenticMode(false);
      localStorage.setItem('agenticMode', 'false');
      sessionStorage.removeItem('copilot_setup_complete_notified');
    }).catch(() => {});

    showMessage({
      text: "Your Gmail account has been disconnected. I've paused the Copilot — reconnect Gmail to resume automatic certificate management.",
      action: { label: 'Reconnect Gmail', onClick: handleGoogleConnect },
      laterLabel: 'Do it later',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmailConnected]);

  const handleAgenticModeToggle = async (enabled: boolean) => {
    if (enabled) {
      if (certCount !== null && certCount === 0) {
        showMessage({
          text: "Upload your first certificate before activating the Copilot — I need something to manage!",
          action: { label: 'Go to Dashboard', onClick: () => navigate('/dashboard') },
        });
        return;
      }
      if (providerCount !== null && providerCount === 0) {
        showMessage({
          text: "Add at least one certificate provider before activating the Copilot. I need to know who to contact for renewals.",
          action: { label: 'Add providers', onClick: () => navigate('/profile') },
        });
        return;
      }
      if (gmailConnected === false) {
        showMessage({
          text: "Connect Gmail before activating the Copilot — I need it to detect new certificates automatically.",
          action: { label: 'Connect Gmail', onClick: handleGoogleConnect },
        });
        return;
      }
    }

    const token = localStorage.getItem('auth_token');
    localStorage.setItem('agenticMode', String(enabled));
    try {
      const res = await fetch(`${config.apiUrl}/api/users/settings/agentic-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) {
        setAgenticMode(enabled);
        if (enabled) runAgenticFlow();
        if (!enabled) sessionStorage.removeItem('copilot_setup_complete_notified');
      } else {
        showMessage({ text: 'I couldn\'t update the Copilot setting. Please try again.' });
      }
    } catch {
      showMessage({ text: 'I couldn\'t update the Copilot setting. Please try again.' });
    }
  };

  const runAgenticFlow = async () => {
    if (agenticRunning) return;
    setAgenticRunning(true);
    setAgenticStep(0);
    try {
      const token = localStorage.getItem('auth_token');
      setAgenticStep(1);
      const rr = await fetch(`${config.apiUrl}/api/reminders/create-all`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reminder_days: leadDays }),
      });
      const rd = await rr.json();
      setAgenticStep(2);
      const er = await fetch(`${config.apiUrl}/api/reminder-emails/generate-all`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const ed = await er.json();
      setAgenticStep(3);
      const sr = await fetch(`${config.apiUrl}/api/reminder-emails/auto-schedule-all`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const sd = await sr.json();
      const results = { reminders: rd.created || 0, emails: ed.created || 0, scheduled: sd.scheduled || 0 };
      setLastRunResults(results);
      setAgenticStep(0);
      setRefreshTrigger(p => p + 1);
      if (results.reminders + results.emails + results.scheduled > 0) {
        showMessage({ text: `All done! Created ${results.reminders} reminders, ${results.emails} email drafts, scheduled ${results.scheduled} emails.` });
      }
    } catch {
      showMessage({ text: 'Copilot encountered an error during the automated run. Please try again.' });
      setAgenticStep(0);
    } finally {
      setAgenticRunning(false);
    }
  };

  const agenticSteps = ['Idle', 'Creating Reminders', 'Generating Emails', 'Scheduling Emails'];

  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const heroBg = palette.tokens.primaryActive;
  const heroOnColor = palette.tokens.onPrimary;
  const heroSubtleColor = alpha(palette.tokens.onPrimary, 0.78);
  const heroAccentColor = palette.tokens.accentAmber;

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      {/* ── TOP ROW: welcome card (3/4) + controls card (1/4), same shape as the Certificates dashboard ── */}
      <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2, mb: 3, alignItems: 'stretch' }}>
        <Box sx={{
          flex: 3, borderRadius: '14px', mb: { xs: 2, md: 0 }, overflow: 'hidden',
          border: `1px solid ${palette.tokens.hairline}`, boxShadow: '0 1px 3px rgba(20,20,19,0.06)',
        }}>
          <Box sx={{ bgcolor: heroBg, p: { xs: 2.7, md: 3.6 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.8, flexWrap: 'wrap' }}>
              <Box sx={{ bgcolor: alpha(heroOnColor, 0.18), borderRadius: 10, px: 1.35, py: 0.36 }}>
                <Typography sx={{ fontSize: '0.65rem', color: heroOnColor, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                </Typography>
              </Box>
            </Box>

            <Typography variant="displaySm" component="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.52rem' }, fontWeight: 400, color: heroOnColor, lineHeight: 1.2, mb: 1.35, fontVariantNumeric: 'lining-nums' }}>
              Good {timeOfDay}.{' '}
              {agenticMode ? (
                <>Your compliance is fully <Box component="span" sx={{ color: heroAccentColor }}>automated.</Box></>
              ) : (
                <>Your compliance is <Box component="span" sx={{ color: heroAccentColor }}>not fully automated.</Box></>
              )}
            </Typography>

            <Typography sx={{ color: heroSubtleColor, fontSize: '0.81rem', lineHeight: 1.6 }}>
              {agenticMode
                ? <>Your AI assistant is making sure you stay compliant by setting up <Box component="span" sx={{ color: heroAccentColor, fontWeight: 700 }}>flows</Box> that follow every certificate renewal.</>
                : <>Switch on your AI assistant to set up <Box component="span" sx={{ color: heroAccentColor, fontWeight: 700 }}>flows</Box> that follow every certificate renewal and keep you compliant.</>}{' '}
              <Typography
                component="span"
                onClick={() => navigate('/ai-flow')}
                sx={{ fontSize: '0.81rem', fontWeight: 700, color: heroOnColor, cursor: 'pointer', textDecoration: 'underline' }}
              >
                See how a flow works →
              </Typography>
            </Typography>

            <Typography sx={{ color: heroSubtleColor, fontSize: '0.81rem', lineHeight: 1.6, mt: 1.8 }}>
              You stay in control: pause it, or personalize it to fit your needs.
            </Typography>
          </Box>

          <Box sx={{ bgcolor: palette.tokens.canvas, p: { xs: 2.7, md: 3.6 } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: { xs: 2.25, sm: 2.7 } }}>
              {[
                { value: flowCounts ? flowCounts.active : '—', label: 'Active flows', color: palette.tokens.ink },
                { value: flowCounts ? flowCounts.paused : '—', label: 'Paused flows', color: flowCounts && flowCounts.paused > 0 ? '#a67a1a' : palette.tokens.ink },
                { value: agenticMode ? 'ON' : 'OFF', label: 'AI assistant status', color: agenticMode ? '#2f7a44' : palette.tokens.mutedSoft },
              ].map(stat => (
                <Box key={stat.label}>
                  <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 0.45 }} />
                  <Typography variant="displaySm" component="div" sx={{ fontSize: { xs: '1.62rem', md: '1.98rem' }, color: stat.color, fontWeight: 700, lineHeight: 1, textAlign: 'center', fontVariantNumeric: 'lining-nums' }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: palette.tokens.muted, mt: 0.45, textAlign: 'center' }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{
            height: '100%', borderRadius: '14px', border: `1px solid ${palette.tokens.hairline}`,
            bgcolor: palette.tokens.surfaceCreamStrong, p: 2, display: 'flex', flexDirection: 'column',
          }}>
          <Box sx={{ bgcolor: palette.tokens.white, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '8px', p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: agenticMode ? palette.tokens.ink : '#374151', flex: 1 }}>
                Mill, AI agent
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: agenticMode ? '#16a34a' : palette.tokens.mutedSoft }}>
                {agenticMode ? 'On' : 'Off'}
              </Typography>
              <Switch checked={agenticMode} onChange={e => handleAgenticModeToggle(e.target.checked)} disabled={agenticRunning} color="primary" size="small" />
            </Box>
            {agenticRunning && (
              <Typography sx={{ color: palette.tokens.primary, fontWeight: 500, fontSize: '0.72rem' }}>{agenticSteps[agenticStep]}</Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9, flex: 1, mt: 3 }}>
              {[
                ...ASSISTANT_FUNCTIONS.map(f => ({
                  key: f.key, label: f.label, description: f.description, on: functionOn(assistantFunctions, f.key),
                  icon: { calendar: <CalendarMonthIcon sx={{ fontSize: 16 }} />, contacts: <EmailIcon sx={{ fontSize: 16 }} />, booking: <EventAvailableIcon sx={{ fontSize: 16 }} />, renewals: <VerifiedIcon sx={{ fontSize: 16 }} /> }[f.key],
                })),
              ].map(item => {
                const active = agenticMode && item.on;
                return (
                  <Tooltip key={item.key} title={item.description} placement="left" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, color: active ? palette.tokens.ink : palette.tokens.mutedSoft }}>
                      <Box sx={{ display: 'flex', color: active ? palette.tokens.primary : palette.tokens.mutedSoft }}>{item.icon}</Box>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, flex: 1 }}>{item.label}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, minWidth: 22, textAlign: 'right', color: active ? '#16a34a' : palette.tokens.mutedSoft }}>{active ? 'ON' : 'OFF'}</Typography>
                      <Switch
                        size="small" color="primary"
                        checked={active}
                        disabled={functionBusy || !agenticMode}
                        onChange={e => handleToggleFunction(item.key, e.target.checked)}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
            <Button size="small" variant="outlined" startIcon={<AIGearIcon sx={{ fontSize: 18 }} />}
              onClick={() => setSettingsOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: '8px', px: 2, py: 0.75, alignSelf: 'flex-start' }}>
              Personalize
            </Button>
          </Box>
          </Box>
        </Box>
      </Box>

      <ActiveReminders key={refreshTrigger} assistantEnabled={agenticMode} assistantFunctions={assistantFunctions} />

      <Dialog open={!!functionPrompt} onClose={() => applyFunctionPrompt(false)} maxWidth="xs" fullWidth>
        {functionPrompt && (
          <>
            <DialogTitle>{promptCopy(functionPrompt).title}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">{promptCopy(functionPrompt).body}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => applyFunctionPrompt(false)}>Not now</Button>
              <Button variant="contained" onClick={() => applyFunctionPrompt(true)}>{promptCopy(functionPrompt).action}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <AssistantSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        currentDays={leadDays}
        currentRecipients={flowRecipients}
        currentFunctions={assistantFunctions}
        token={localStorage.getItem('auth_token') || ''}
        onSaved={(days, flowsUpdated, recipients, functions) => {
          setLeadDays(days);
          setFlowRecipients(recipients);
          setAssistantFunctions(functions);
          setRefreshTrigger(p => p + 1);
          showMessage({
            text: flowsUpdated !== null
              ? `Done! New flows will start ${days} days before expiry, and I updated ${flowsUpdated} existing flow${flowsUpdated === 1 ? '' : 's'}.`
              : 'Done! Your AI assistant settings are saved.',
          });
        }}
      />
    </Box>
  );
};

export default RemindersPage;
